"use client";

import { useState } from "react";
import { ColumnDef } from "@tanstack/react-table";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { DataTable } from "@/components/ui/data-table";
import { Badge } from "@/components/ui/badge";
import { MoreHorizontal, Pencil, Trash2 } from "lucide-react";
import { JobProfile } from "../types";
import {
  languageLevels,
  driversLicenses,
  furtherEducations,
  positions
} from "../data";

interface JobProfileTableProps {
  profiles: JobProfile[];
  onEdit: (profile: JobProfile) => void;
  onDelete: (profile: JobProfile) => void;
  onAdd: () => void;
}

export function JobProfileTable({
  profiles,
  onEdit,
  onDelete,
  onAdd
}: JobProfileTableProps) {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [profileToDelete, setProfileToDelete] = useState<JobProfile | null>(
    null
  );

  const getLanguageLevelLabel = (value: string) => {
    const level = languageLevels.find((level) => level.value === value);
    return level ? level.label : value;
  };

  const getDriversLicenseLabel = (value: string) => {
    const license = driversLicenses.find((license) => license.value === value);
    return license ? license.label : value;
  };

  const getFurtherEducationLabel = (value: string) => {
    const education = furtherEducations.find(
      (education) => education.value === value
    );
    return education ? education.label : value;
  };

  const getPositionLabel = (value: string) => {
    const position = positions.find((position) => position.value === value);
    return position ? position.label : value;
  };

  const columns: ColumnDef<JobProfile>[] = [
    {
      accessorKey: "position",
      header: "Position",
      cell: ({ row }) => {
        const position = row.getValue("position") as string;
        return <Badge variant="outline">{getPositionLabel(position)}</Badge>;
      }
    },
    {
      accessorKey: "language_levels",
      header: "Sprachkenntnisse",
      cell: ({ row }) => {
        const languageLevels = row.getValue("language_levels") as string[];
        return (
          <div className="flex flex-wrap gap-1">
            {languageLevels.map((level, index) => (
              <Badge key={index} variant="outline">
                {getLanguageLevelLabel(level)}
              </Badge>
            ))}
          </div>
        );
      }
    },
    {
      accessorKey: "drivers_licenses",
      header: "Führerschein",
      cell: ({ row }) => {
        const driversLicenses = row.getValue("drivers_licenses") as string[];
        return (
          <div className="flex flex-wrap gap-1">
            {driversLicenses.map((license, index) => (
              <Badge key={index} variant="outline">
                {getDriversLicenseLabel(license)}
              </Badge>
            ))}
          </div>
        );
      }
    },
    {
      accessorKey: "further_educations",
      header: "Weiterbildungen",
      cell: ({ row }) => {
        const furtherEducations = row.getValue(
          "further_educations"
        ) as string[];
        return (
          <div className="flex flex-wrap gap-1">
            {furtherEducations.map((education, index) => (
              <Badge key={index} variant="outline">
                {getFurtherEducationLabel(education)}
              </Badge>
            ))}
          </div>
        );
      }
    },
    {
      id: "actions",
      enableHiding: false,
      cell: ({ row }) => {
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <MoreHorizontal size={16} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(row.original);
                }}
              >
                <Pencil className="mr-2 h-4 w-4" />
                Bearbeiten
              </DropdownMenuItem>
              <DropdownMenuItem
                className="text-red-600"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setProfileToDelete(row.original);
                  setDeleteDialogOpen(true);
                }}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Löschen
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      }
    }
  ];

  const handleDelete = () => {
    if (profileToDelete) {
      onDelete(profileToDelete);
      setDeleteDialogOpen(false);
      setProfileToDelete(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium">Profile</h3>
        <Button onClick={onAdd}>Profil hinzufügen</Button>
      </div>

      {profiles.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          Keine Profile gefunden
        </div>
      ) : (
        <DataTable
          columns={columns}
          data={profiles}
          searchKey="language_levels"
          filterPlaceholder="Profile filtern..."
        />
      )}

      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Profil löschen</DialogTitle>
            <DialogDescription>
              Bist du sicher, dass du dieses Profil löschen möchtest? Diese
              Aktion kann nicht rückgängig gemacht werden.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
            >
              Abbrechen
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Löschen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
